import React from 'react';
import PublicHeader from './PublicHeader';

interface CodeOfConductPageProps {
  onNavigateHome?: () => void;
  onNavigateToMission?: () => void;
  onNavigateToRules?: () => void;
  onNavigateToWaiver?: () => void;
}

const CodeOfConductPage: React.FC<CodeOfConductPageProps> = ({ onNavigateHome, onNavigateToMission, onNavigateToRules, onNavigateToWaiver }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
      {/* Header */}
      <PublicHeader title="Code of Conduct" />

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-fishing-blue mb-2">Tumbulgum Fishing Club Inc.</h2>
            <p className="text-gray-600">NSW 2201262</p>
            <h3 className="text-2xl font-bold text-gray-800 mt-4">Code of Conduct</h3>
          </div>

          {/* Main Conduct Rules */}
          <div className="space-y-6 mb-12">
            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">1.</span>
              <div>
                <span className="font-semibold text-gray-800">Upholding Club Interests:</span>
                <span className="text-gray-700"> Members must always act in the best interests of the Club and its values.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">2.</span>
              <div>
                <span className="font-semibold text-gray-800">Authority to Speak:</span>
                <span className="text-gray-700"> Only Members with express prior approval from the Committee can speak on behalf of the Association.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">3.</span>
              <div>
                <span className="font-semibold text-gray-800">Primary Activity:</span>
                <span className="text-gray-700"> Members must prioritise recreational fishing and comply with NSW Fishery Laws, including valid fishing licences, maximum catch and size limits, and all Maritime Services rules and regulations.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">4.</span>
              <div>
                <span className="font-semibold text-gray-800">Prizes and Judging:</span>
                <span className="text-gray-700"> Prizes and awards for fishing outcomes are determined by the Committee or appointed judges. Members agree that all judges' decisions are final, and no correspondence will be entered into.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">5.</span>
              <div>
                <span className="font-semibold text-gray-800">Risk Awareness:</span>
                <span className="text-gray-700"> Members must recognise the inherent dangers of recreational fishing, including weather, tides, swells, currents, and slippery surfaces. Timely assessments of conditions are essential, especially when Junior Members are involved.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">6.</span>
              <div>
                <span className="font-semibold text-gray-800">Supervision of Juniors:</span>
                <span className="text-gray-700"> Junior Members must always be supervised by adult Members or responsible adults during Club activities, including non-fishing events.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">7.</span>
              <div>
                <span className="font-semibold text-gray-800">Catch, Click, and Release:</span>
                <span className="text-gray-700"> The Club operates a catch, click, and release policy on Competition days. Photos submitted for judging must be fair and honest. Violations may be reviewed by the Committee. Fish can be kept but releasing your catch is encouraged. ALL NSW DPI Fisheries rules apply to catch.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">8.</span>
              <div>
                <span className="font-semibold text-gray-800">Respect for All:</span>
                <span className="text-gray-700"> All Members must treat each other with respect and avoid intimidation, abuse, or making others feel unwelcome.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">9.</span>
              <div>
                <span className="font-semibold text-gray-800">Alcohol and Behaviour:</span>
                <span className="text-gray-700"> During events where alcohol is served, members must avoid bad language, lewd behavior, and ensure no negative exposure to Junior Members. Violations may result in refusal of alcohol service, removal from the event, or disciplinary action.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">10.</span>
              <div>
                <span className="font-semibold text-gray-800">Representation of Club:</span>
                <span className="text-gray-700"> When wearing official Club attire, Members must avoid behavior that reflects poorly on the Tumbulgum Fishing Club.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">11.</span>
              <div>
                <span className="font-semibold text-gray-800">Safety:</span>
                <span className="text-gray-700"> Members enjoyment & comradery is paramount within the Tumbulgum Fishing Club but Members safety over-rides everything. Please look out for yourselves and others at all times. Dangerous behaviour will never be accepted.</span>
              </div>
            </div>

            <div className="flex">
              <span className="font-bold text-fishing-blue mr-3">12.</span>
              <div>
                <span className="font-semibold text-gray-800">Social Media use:</span>
                <span className="text-gray-700"> Club members must not post material that is offensive toward any group or person based on personal traits, attributes, beliefs, or practices that exploit, objectify, or are derogatory of gender, ethnicity, or religion. Such behaviour involving social media may amount to conduct that could constitute prejudicial conduct or unacceptable behaviour if the member acts or omits to act in a way that is likely to prejudice the club or bring discredit upon it.</span>
              </div>
            </div>
          </div>

          {/* Unacceptable Behaviour Section */}
          <div className="border-t-4 border-fishing-blue pt-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">What is Unacceptable Behaviour?</h3>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              Unacceptable behaviour in the club is unreasonable conduct at work or in any situation that may be connected to the club that is offensive, belittling, abusive or threatening to another person or adverse to moral, discipline or club cohesion. Unacceptable behaviour may result in psychological injury. Unacceptable behaviour can have a significant impact on the mental health of people involved, and is categorised under seven general headings:
            </p>

            <div className="space-y-8">
              {/* Harassment */}
              <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
                <h4 className="text-xl font-bold text-gray-800 mb-3">Harassment</h4>
                <p className="text-gray-700 leading-relaxed">
                  It is unwanted or unwelcome behaviour that a reasonable person, having regard to all the circumstances, would consider offensive, insulting, humiliating or intimidating. There does not have to be an intention to offend or harass for harassment to occur. Harassing behaviour can range from serious to less significant; one-off incidents may still constitute harassment.
                </p>
              </div>

              {/* Bullying */}
              <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
                <h4 className="text-xl font-bold text-gray-800 mb-3">Bullying</h4>
                <p className="text-gray-700 leading-relaxed">
                  Bullying is an aggressive form of harassment. Bullying is a persistent, unreasonable pattern of behaviour directed towards a person or group of persons, which may create a risk to health and safety, including a risk to the emotional, mental or physical health of the person(s) in the workplace. Club members at all levels may be affected, not only those to whom the harassment is directed, for example, colleagues.
                </p>
              </div>

              {/* Sexual Misconduct */}
              <div className="bg-purple-50 border-l-4 border-purple-600 p-6 rounded-r-lg">
                <h4 className="text-xl font-bold text-gray-800 mb-3">Any Form of Sexual Misconduct</h4>
                <p className="text-gray-700 leading-relaxed">
                  Sexual misconduct is the term used to cover the full spectrum of inappropriate behaviours of a sexual nature, from unacceptable behaviours that are visible and non-criminal, through to criminal behaviours. These behaviours can be committed by force or intimidation or be unwelcome. Sexual misconduct includes but is not limited to: sexual discrimination; sexual harassment; sexual offences; and the recording, photographing or transmitting of incidents of a sexual nature without the knowledge and consent of all parties.
                </p>
              </div>

              {/* Discrimination */}
              <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 rounded-r-lg">
                <h4 className="text-xl font-bold text-gray-800 mb-3">Discrimination</h4>
                <p className="text-gray-700 leading-relaxed">
                  Any distinction, exclusion or preference that has the effect of nullifying or impairing equality of opportunity or treatment in employment or occupation. Discrimination can include racial, sex, disability or age. Unlawful discrimination does not include any distinction, exclusion or preference in respect of a particular job based on the inherent requirements of the job.
                </p>
              </div>

              {/* Abuse of Power */}
              <div className="bg-indigo-50 border-l-4 border-indigo-600 p-6 rounded-r-lg">
                <h4 className="text-xl font-bold text-gray-800 mb-3">Abuse of Power</h4>
                <p className="text-gray-700 leading-relaxed">
                  Abuse of power and authority attributed to position to harass, discriminate or bully a subordinate is unacceptable, unethical and in some situations can constitute criminal behaviour.
                </p>
              </div>

              {/* Inappropriate Relationships */}
              <div className="bg-pink-50 border-l-4 border-pink-600 p-6 rounded-r-lg">
                <h4 className="text-xl font-bold text-gray-800 mb-3">Inappropriate Workplace Relationships and Conflict of Interest</h4>
                <p className="text-gray-700 leading-relaxed">
                  Any relationship that involves, or gives the appearance of involving, partiality, preferential treatment or improper use of position.
                </p>
              </div>

              {/* Violent Behaviour */}
              <div className="bg-red-100 border-l-4 border-red-800 p-6 rounded-r-lg">
                <h4 className="text-xl font-bold text-gray-800 mb-3">Violent Behaviour</h4>
                <p className="text-gray-700 leading-relaxed">
                  Is the intentional use of physical force, threatened or actual, against oneself, another person, or against a group or community or property, which either results in or has a high likelihood of resulting in injury, death, or psychological harm. Violent behaviour can have far-reaching effects on club members, their family, the workplace and the existence of the club. Violent behaviour, inside or outside the workplace, is unacceptable.
                </p>
              </div>
            </div>
          </div>

          {/* Closing Statement */}
          <div className="mt-12 p-6 bg-fishing-blue text-white rounded-lg text-center">
            <p className="text-xl font-semibold">
              Be proud of your Club, its endeavours, and its achievements.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-4 mt-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Contact Us</h3>
              <p className="text-gray-300">Website: www.tumbyfc.com</p>
              <p className="text-gray-300">Email: fishing@tumbyfc.com</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><button onClick={onNavigateHome} className="text-gray-300 hover:text-white">Home</button></li>
                <li><button onClick={onNavigateToMission} className="text-gray-300 hover:text-white">Mission, Values & Vision</button></li>
                <li><button onClick={onNavigateToRules} className="text-gray-300 hover:text-white">Club Rules</button></li>
                <li><button onClick={onNavigateToWaiver} className="text-gray-300 hover:text-white">Waiver & Release</button></li>
                <li><a href="/Constitution-Tumbulgum-Fishing-Club-V5.pdf" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white">Club Constitution</a></li>
                <li><button className="text-gray-300 hover:text-white">Privacy Policy</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Location</h3>
              <p className="text-gray-300">Tumbulgum, New South Wales</p>
              <p className="text-gray-300">Australia</p>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">&copy; 2026 Tumbulgum Fishing Club. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CodeOfConductPage;
